import { useEffect, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SectionLabel } from './ui'
import MemberPicker from './MemberPicker'
import PersonAvatarFace from '../PersonAvatarFace'
import { useAvatars } from '../../lib/useAvatars'
import { Skeleton } from '../Home/ui'

export default function HeadToHead({ stagionale, leagueId }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const [uA, setUA] = useState('')
  const [uB, setUB] = useState('')
  const avatars = useAvatars(stagionale.map(m => m.out_id_user))
  const [gare, setGare] = useState([])
  const [score, setScore] = useState({ wA: 0, wB: 0, draw: 0 })
  const [loadingH2H, setLoadingH2H] = useState(false)

  const nameA = stagionale.find(m => m.out_id_user === uA)?.out_display_name ?? ''
  const nameB = stagionale.find(m => m.out_id_user === uB)?.out_display_name ?? ''

  useEffect(() => {
    if (!uA || !uB) { setGare([]); setScore({ wA: 0, wB: 0, draw: 0 }); return }
    setLoadingH2H(true)
    supabase
      .rpc('fn_head_to_head', { p_id_league: leagueId, p_user_a: uA, p_user_b: uB })
      .then(({ data }) => {
        const rows = data ?? []
        setGare(rows)
        let wA = 0, wB = 0, draw = 0
        rows.forEach(g => {
          if (g.out_pos_a < g.out_pos_b) wA++
          else if (g.out_pos_b < g.out_pos_a) wB++
          else draw++
        })
        setScore({ wA, wB, draw })
        setLoadingH2H(false)
      })
  }, [uA, uB, leagueId])

  return (
    <Card>
      <View style={{ marginBottom: 16 }}>
        <SectionLabel>{t('leagues.head2head')}</SectionLabel>
      </View>

      <View style={styles.pickerRow}>
        <MemberPicker members={stagionale} value={uA} onChange={setUA} exclude={uB} placeholder={t('leagues.player_a')} title={t('leagues.player_a')} style={{ flex: 1 }} />
        <Text style={[styles.vs, { color: tc.rosaText }]}>VS</Text>
        <MemberPicker members={stagionale} value={uB} onChange={setUB} exclude={uA} placeholder={t('leagues.player_b')} title={t('leagues.player_b')} style={{ flex: 1 }} />
      </View>

      {uA && uB && loadingH2H && (
        <View style={{ gap: 10, paddingVertical: 8 }} accessible accessibilityLabel={t('common.loading')} accessibilityState={{ busy: true }}>
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
        </View>
      )}

      {uA && uB && !loadingH2H && gare.length > 0 && (
        <>
          <View style={[styles.scoreBox, { backgroundColor: tc.bgCard2 }]}>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <PersonAvatarFace userId={uA} settings={avatars[uA]} size={44} />
              <Text style={[styles.scoreName, { color: tc.textMuted, marginTop: 6 }]} numberOfLines={1}>{nameA}</Text>
              <Text style={[styles.scoreNum, { color: score.wA > score.wB ? tc.rosaText : tc.text }]}>{score.wA}</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              {score.draw > 0 && (
                <Text style={[styles.drawText, { color: tc.textMuted }]}>
                  {t('leagues.draws', { count: score.draw })}
                </Text>
              )}
              <Text style={[styles.onRaces, { color: tc.textMuted }]}>
                {t('leagues.on_races', { count: gare.length })}
              </Text>
            </View>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <PersonAvatarFace userId={uB} settings={avatars[uB]} size={44} />
              <Text style={[styles.scoreName, { color: tc.textMuted, marginTop: 6 }]} numberOfLines={1}>{nameB}</Text>
              <Text style={[styles.scoreNum, { color: score.wB > score.wA ? tc.rosaText : tc.text }]}>{score.wB}</Text>
            </View>
          </View>

          <View style={{ gap: 6 }}>
            {gare.map((g, i) => (
              <View key={i} style={[styles.raceRow, { backgroundColor: tc.bgCard2 }]}>
                <Text style={[styles.racePos, { color: g.out_pos_a < g.out_pos_b ? tc.rosaText : tc.textMuted }]}>
                  {g.out_pos_a}°
                </Text>
                <Text style={[styles.raceName, { color: tc.textMuted }]} numberOfLines={1}>
                  {g.out_race_name}
                </Text>
                <Text style={[styles.racePos, { color: g.out_pos_b < g.out_pos_a ? tc.rosaText : tc.textMuted }]}>
                  {g.out_pos_b}°
                </Text>
              </View>
            ))}
          </View>
        </>
      )}

      {uA && uB && !loadingH2H && gare.length === 0 && (
        <Text style={[styles.noCommon, { color: tc.textMuted }]}>{t('leagues.no_common')}</Text>
      )}
    </Card>
  )
}

const styles = StyleSheet.create({
  pickerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 },
  vs: { fontFamily: fonts.condensedBold, fontSize: 18, flexShrink: 0 },
  scoreBox: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, borderRadius: 12, padding: 14, paddingHorizontal: 16, marginBottom: 16 },
  scoreName: { fontSize: 12, marginBottom: 4 },
  scoreNum: { fontSize: 36, fontFamily: fonts.condensedBold },
  drawText: { fontSize: 11 },
  onRaces: { fontSize: 12, fontWeight: '700' },
  raceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderRadius: 8, padding: 8, paddingHorizontal: 12, marginBottom: 6 },
  racePos: { fontSize: 16, fontFamily: fonts.condensedBold, width: 36, textAlign: 'center' },
  raceName: { flex: 1, fontSize: 10, textAlign: 'center' },
  noCommon: { fontSize: 13, textAlign: 'center', padding: 16 },
})
